import { router } from "@inertiajs/react";
import { round1 } from "./scoreMath";
import type { HeatmapTileStatus } from "./tileColors";
import type { HeatmapScore, HeatmapGroup } from "./heatmapTypes";

export function gapPct(company: number | null, peers: number | null) {
  if (!Number.isFinite(company) || !Number.isFinite(peers) || peers === 0)
    return 0;
  const safeCompany = company as number;
  const safePeers = peers as number;
  return (safePeers - safeCompany) / safePeers;
}

export function statusKey(
  company: number | null,
  peers: number | null,
  threshold: number,
): HeatmapTileStatus {
  if (!Number.isFinite(company) || !Number.isFinite(peers)) return "no_data";
  const gap = gapPct(company, peers);
  if (gap < 0) return "beat";
  if (gap <= threshold) return "close";
  return "lag";
}

export function gapLabel(company: number | null, peers: number | null) {
  if (!Number.isFinite(company) || !Number.isFinite(peers) || peers === 0)
    return null;
  const gap = gapPct(company, peers);
  if (gap < 0) return `${Math.abs(Math.round(gap * 100))}% better`;
  if (gap === 0) return "On par";
  return `${Math.round(gap * 100)}% behind`;
}

export function readTileHash(): string | null {
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) return null;
  const p = new URLSearchParams(raw);
  return p.get("tile");
}

export function writeTileHash(tileId: string) {
  const p = new URLSearchParams();
  p.set("tile", tileId);
  window.location.hash = `#${p.toString()}`;
}

export function clearTileHash() {
  const url = new URL(window.location.href);
  url.hash = "";
  window.history.replaceState({}, "", url.toString());
}


export function clampScore(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.max(0, Math.min(5, round1(v)));
}

export function groupAverages(
  accountId: number,
  group: HeatmapGroup,
  getScore: (accountId: number, tileId: string) => HeatmapScore,
) {
  let companySum = 0;
  let peersSum = 0;
  let nCompany = 0;
  let nPeers = 0;

  for (const tile of group.tiles) {
    const score = getScore(accountId, tile.id);
    if (Number.isFinite(score.company)) {
      companySum += score.company as number;
      nCompany += 1;
    }
    if (Number.isFinite(score.peers)) {
      peersSum += score.peers as number;
      nPeers += 1;
    }
  }

  return {
    company: nCompany ? round1(companySum / nCompany) : null,
    peers: nPeers ? round1(peersSum / nPeers) : null,
  };
}

export function navigateToUrl(url: string) {
  if (!url) return;
  if (url.startsWith("/")) {
    router.visit(url);
  } else {
    window.location.assign(url);
  }
}

export function kpiQuestionsForTile(
  mapping: Record<string, Record<string, string[]>> | undefined,
  groupId: string,
  tileId: string,
): string[] {
  const group = mapping?.[groupId];
  const tile = group?.[tileId];
  if (!Array.isArray(tile)) return [];
  return tile.filter(
    (x): x is string => typeof x === "string" && x.trim().length > 0,
  );
}
